<template>
  <div class="login-page">
    <div class="login-layout">

      <!-- ── LEFT PANEL ── -->
      <div class="left-panel">
        <!-- Top: Logo -->
        <div class="lp-top">
          <div class="lp-logo">
            <div class="lp-logo-box">
              <img src="../../assets/img/logo_icon.png" alt="InternLink" />
            </div>
            <span class="lp-logo-text">InternLink</span>
          </div>
        </div>

        <!-- Middle: Tagline -->
        <div class="lp-hero">
          <div class="lp-tagline">
            Bridging Academy
            <span class="lp-tagline-green">to Industry Success.</span>
          </div>
          <div class="lp-caption">
            Streamline your OJT Management. Connect students with top-tier
            partners, validate deliverables in real-time, and ensure career
            readiness.
          </div>
        </div>

        <!-- Bottom: Features + Footer -->
        <div class="lp-bottom">
          <div class="lp-features">
            <div class="lp-feature-item">
              <v-icon color="#5ee9b5" size="20">mdi-check-circle-outline</v-icon>
              <span>Smart AI Student Matching</span>
            </div>
            <div class="lp-feature-item">
              <v-icon color="#5ee9b5" size="20">mdi-shield-check-outline</v-icon>
              <span>Secure Document Validation</span>
            </div>
            <div class="lp-feature-item">
              <v-icon color="#5ee9b5" size="20">mdi-lightning-bolt-outline</v-icon>
              <span>Real-time Performance Monitoring</span>
            </div>
          </div>
          <hr class="lp-divider" />
          <div class="lp-footer">
            <span>© 2026 InternLink Systems. All rights reserved.</span>
            <div class="lp-footer-links">
              <span>Privacy Policy</span>
              <span>Terms of Service</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── RIGHT PANEL ── -->
      <div class="right-panel">
        <div class="login-card rounded-xl">

          <!-- App icon -->
          <div class="d-flex justify-center mb-5">
            <div class="card-logo-box rounded-xl">
              <img src="../../assets/img/internLink_logo.png" alt="InternLink" width="44" />
            </div>
          </div>

          <!-- Headings -->
          <div class="lc-title mb-1">Welcome back</div>
          <div class="lc-subtitle mb-6">
            Enter your credentials to access your account
          </div>

          <!-- Form -->
          <v-form ref="formLogin" class="w-100" @submit.prevent="dologin">

            <!-- Email field -->
            <div class="field-group mb-4">
              <div class="field-label">Email Address</div>
              <v-text-field
                v-model="emailLogin"
                :rules="[formRules.required, formRules.email]"
                placeholder="name@university.edu.ph"
                prepend-inner-icon="mdi-email-outline"
                :color="$vuetify.theme.themes.light.inputColor"
                variant="outlined"
                @keyup.enter="dologin()"
                density="comfortable"
                hide-details="auto"
                rounded="lg"
              />
            </div>

            <!-- Password field -->
            <div class="field-group mb-5">
              <div class="d-flex justify-space-between align-center mb-1">
                <div class="field-label">Password</div>
                <a class="forgot-link">Forgot password?</a>
              </div>
              <v-text-field
                v-model="passwordLogin"
                :rules="[formRules.required, formRules.password]"
                placeholder="••••••••"
                prepend-inner-icon="mdi-lock-outline"
                :type="showPassword ? 'text' : 'password'"
                :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append-inner="showPassword = !showPassword"
                @keyup.enter="dologin()"
                :color="$vuetify.theme.themes.light.inputColor"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                rounded="lg"
              />
            </div>

            <!-- Sign In button -->
            <v-btn
              rounded="pill"
              color="#10b981"
              :loading="btnLoading"
              :disabled="btnLoading"
              block
              size="large"
              class="text-white sign-in-btn mb-5"
              @click="dologin()"
              append-icon="mdi-arrow-right"
            >
              Sign In
            </v-btn>

            <!-- Activate account -->
            <div class="text-caption text-center text-grey-darken-1">
              First time here?
              <a class="activate-link">Activate your account</a>
            </div>
          </v-form>
        </div>
      </div>
    </div>

    <fade-away-message-component
      :displayType="'variation2'"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import { useDisplay } from "vuetify";
export default {
  name: "loginPage",
  data() {
    return {
      display: useDisplay(),
      isLogin: true,
      mobile: true,
      btnLoading: false,
      showPassword: false,
      showPasswordConfirm: false,
      step: { id: 1, name: "Personal Information" },
      fname: null,
      lname: null,
      mname: null,
      name: "",
      email: "",
      emailLogin: "",
      passwordLogin: "",
      password: "",
      emailError: "",
      confirmPassword: "",
      userType: null,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
    };
  },
  computed: {
    isSmAndUp() {
      return this.display.smAndUp;
    },
  },
  mounted() {
    this.userType = localStorage.getItem("userType");
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    async dologin() {
      let isValidated = await this.$refs.formLogin.validate();
      if (isValidated) {
        this.btnLoading = true;
        let data = {
          email: this.emailLogin,
          password: this.passwordLogin,
        };
        this.axiosCall("/auth/login", "POST", data).then((res) => {
          this.btnLoading = false;
          if (!res) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.message = "Cannot connect to server. Please try again.";
            this.fadeAwayMessage.header = "Connection Error";
            return;
          }
          if (
            res.data.status == 200 ||
            res.data.status == 201 ||
            res.data.status == 202
          ) {
            localStorage.setItem("token", res.data.token);
            this.$store.commit("setExpiryDate");
            this.$store.dispatch("setIsAuthenticated", 1);
            location.reload();
          } else {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.message = res.data.message;
            this.fadeAwayMessage.header = "System Message";
          }
        });
      }
    },

    register() {
      if (this.password != this.confirmPassword) {
        this.fadeAwayMessage.show = true;
        this.fadeAwayMessage.type = "error";
        this.fadeAwayMessage.message = "Password not mutch";
        this.fadeAwayMessage.header = "System Message";
      } else {
        if (this.$refs.formRegister.validate()) {
          let data = {
            fname: this.fname,
            mname: this.mname,
            lname: this.lname,
            user_roleID: this.userType == 0 ? 3 : 2,
            isAdminApproved: this.userType == 0 ? 1 : 0,
            usertypeID: this.userType == 0 ? 7 : 2,
            email: this.email,
            password: this.password,
          };
          this.axiosCall("/auth/registerUser", "POST", data).then((res) => {
            if (res.data.status == 201) {
              this.isLoading = false;
              this.$store.dispatch("setEmail", this.email);
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "success";
              this.fadeAwayMessage.message = res.data.message;
              this.fadeAwayMessage.header = "System Message";
              this.isLogin = true;
              this.refresh();
            } else {
              this.isLoading = false;
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "error";
              this.fadeAwayMessage.message = res.data.message;
              this.fadeAwayMessage.header = "System Message";
              this.animated = true;
            }
          });
        }
      }
    },

    checkEmail() {
      this.emailChecking = true;
      this.axiosCall("/auth/checkEmailIfExist/" + this.email, "GET").then(
        (res) => {
          if (res.data) {
            this.emailError = "Email Already Exist!";
          } else {
            this.emailError = "";
          }
          this.emailChecking = false;
        },
      );
    },
    refresh() {
      this.lname = null;
      this.fname = null;
      this.mname = null;
      this.email = null;
      this.password = null;
    },
    next() {
      if (this.step.id == 1) {
        if (this.$refs.formInformation.validate()) {
          if (this.fname == null || this.lname == null) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.message = "Please fill-up required fields!";
            this.fadeAwayMessage.header = "System Message";
          } else {
            this.step = { id: 2, name: "Account Information" };
          }
        }
      }
    },
  },
};
</script>

<style scoped>
/* ── Page wrapper ── */
.login-page {
  height: 100vh;
  overflow: hidden;
}

.login-layout {
  display: flex;
  height: 100%;
}

/* ══════════════════════════════
   LEFT PANEL
══════════════════════════════ */
.left-panel {
  width: 50%;
  height: 100%;
  background-image: url("../../assets/img/Container.png");
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 40px;
}

/* Logo row */
.lp-logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.lp-logo-box {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.lp-logo-box img {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.lp-logo-text {
  color: white;
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.3px;
}

/* Tagline */
.lp-hero {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-bottom: 32px;
}

.lp-tagline {
  color: white;
  font-size: 46px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -1px;
  width: 58%;
  margin-bottom: 18px;
}

.lp-tagline-green {
  color: #5ee9b5;
  display: block;
}

.lp-caption {
  color: #d0fae5e5;
  font-size: 16px;
  line-height: 1.7;
  width: 54%;
  letter-spacing: -0.2px;
}

/* Features + footer */
.lp-bottom {
  color: #a4f4cf99;
}

.lp-features {
  margin-bottom: 28px;
}

.lp-feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 16px;
}

.lp-divider {
  background-color: rgba(164, 244, 207, 0.25);
  height: 1px;
  border: 0;
  margin-bottom: 12px;
}

.lp-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #a4f4cf80;
}

.lp-footer-links {
  display: flex;
  gap: 24px;
}

/* ══════════════════════════════
   RIGHT PANEL
══════════════════════════════ */
.right-panel {
  flex: 1;
  height: 100%;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.login-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 420px;
  padding: 40px 36px;
  text-align: center;
}

/* Card logo */
.card-logo-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Welcome text */
.lc-title {
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.3;
}

.lc-subtitle {
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
}

/* Field groups */
.field-group {
  text-align: left;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.forgot-link {
  font-size: 12px;
  font-weight: 500;
  color: #10b981;
  cursor: pointer;
  text-decoration: none;
}

.forgot-link:hover {
  text-decoration: underline;
}

/* Sign-in button */
.sign-in-btn {
  font-weight: 600;
  letter-spacing: 0.2px;
}

/* Activate account */
.activate-link {
  color: #10b981;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}

.activate-link:hover {
  text-decoration: underline;
}

/* ── Responsive: hide left panel on small screens ── */
@media (max-width: 959px) {
  .left-panel {
    display: none;
  }
  .right-panel {
    width: 100%;
  }
  .login-card {
    padding: 32px 24px;
  }
}

@media (max-width: 479px) {
  .lc-title {
    font-size: 22px;
  }
  .login-card {
    padding: 28px 20px;
    border-radius: 12px !important;
  }
}
</style>
